/**
 * `mountRenderer` — the WP6 top-level API consumed by WP7 (and by our
 * own demo harness + Playwright smoke).
 *
 * The renderer OWNS nothing about WHERE snapshots come from — it takes
 * a `SnapshotSource` and drives the render loop off of it. WP7 will
 * pass a WS-backed source; tests and the demo pass fixture-backed
 * sources. This keeps the WS client, React overlay, and input handling
 * strictly out of WP6.
 */

import type { ChampionId, WireSimState } from "@pit/protocol";
import {
  applyCameraTransform,
  createStage,
  setArena as setStageArena,
  type ArenaGeometry,
} from "./stage.js";
import {
  STEALTH_VISIBLE_ALPHA,
  renderFrame,
  renderProjectiles,
  renderWalls,
  type EntityView,
  type LoadedChampionSheet,
  type ProjectileView,
  type WallView,
} from "./entityView.js";
import { interpolate, type InterpolatedEntity, type InterpolatedState } from "./interpolate.js";
import { computeCameraCenter, type CameraMode } from "./camera.js";
import { hudReserveBottomPx } from "./hudLayout.js";
import { CombatTextManager } from "./combatTextManager.js";
import {
  DEFAULT_CHAT_DISPLAY_POLICY,
  visibleChatForViewer,
  type ChatDisplayPolicy,
} from "./chatDisplay.js";
import { DEFAULT_COMBAT_TEXT_STYLE, type CombatTextStyle } from "./combatText.js";
import { AbilityVisualManager } from "./abilityVisualManager.js";
import { ProjectileImpactManager } from "./projectileImpactManager.js";
import { MeleeAttackAnimationTracker } from "./meleeAttackAnimation.js";
import { DEFAULT_NAMEPLATE_STYLE, type NameplateStyle } from "./nameplate.js";
import { DEFAULT_SPELL_NAME_STYLE, type SpellNameStyle } from "./spellNameLabel.js";
import {
  CHARACTER_TARGET_HEIGHT_UNITS,
  NO_OFFSET,
  computeArenaCenter,
  projectHeight,
  type ProjectionCenter,
} from "./projection.js";
import {
  has3DModel,
  model3DFor,
  model3DUrl,
  CHAMPION_IDS_WITH_3D_MODEL,
} from "./three3d/models.js";
import { parseCharacterModel, type LoadedCharacterModel } from "./three3d/characterModel.js";
import { prop3DFor, prop3DUrl, propHeightUnits } from "./three3d/props.js";
import { loadPropModel, type LoadedPropModel } from "./three3d/propModel.js";
import {
  createThreeCharacterLayer,
  type Character3DInput,
  type Prop3DInput,
  type ThreeCharacterLayer,
} from "./three3d/threeLayer.js";
import { DEFAULT_KEYMAP, type Keymap } from "../input/keybinding/index.js";
import { isHiddenFromViewer, isStealthedVisibleToViewer } from "../input/stealthVisibility.js";
import { ImpactAudioManager, type ImpactAudioPlayer } from "../sound/impactAudio.js";
import { createGraphicsCadence } from "./cadence.js";
import {
  DEFAULT_INTERP_DELAY_TICKS,
  InterpolationClock,
  SnapshotBuffer,
  type SnapshotEntry,
} from "../net/snapshots.js";

/**
 * Handle returned by `SnapshotSource.subscribe`. Consumers call
 * `dispose()` when the renderer is torn down.
 */
export interface SnapshotSourceHandle {
  readonly dispose: () => void;
}

/**
 * Snapshot ingress abstraction. The source pushes `SnapshotEntry`s to
 * a listener; the renderer stores them in its ring buffer.
 *
 * WP7's WS client implements this backed by socket messages. Tests
 * implement it backed by an in-memory array. The interface is
 * intentionally minimalistic (one callback, one dispose).
 */
export interface SnapshotSource {
  subscribe(onSnapshot: (entry: SnapshotEntry) => void): SnapshotSourceHandle;
}

/** Runtime handle for a mounted renderer. Callers dispose to tear down. */
export interface Renderer {
  /** Detach snapshot source, destroy Pixi, remove canvas. */
  readonly dispose: () => void;
  /** Directly push a snapshot — useful for tests that don't want a
   *  source object. */
  readonly pushSnapshot: (entry: SnapshotEntry) => void;
  /** Force a single frame render at the current clock. Bypasses rAF —
   *  used by Playwright to grab a deterministic screenshot. */
  readonly renderOnce: () => void;
  /** Update which seat the camera follows (seat can arrive after mount).
   *  `null` means "not yet known" (#731) — the follow camera holds arena
   *  center and no entity is treated as the viewer's own rather than
   *  falling back to seat 0. */
  readonly setOwnSeat: (seat: number | null) => void;
  /** #702 follow-up: swap the mounted arena geometry post-mount (redraws
   *  floor/edge/spawn/pillar art; the per-frame camera picks up the new
   *  dimensions automatically). Needed because `matchCreated`/`matchPhase`'s
   *  `mapSlug` can arrive AFTER this renderer already mounted (most likely
   *  for a joining teammate) — see `stage.ts`'s `setArena` doc comment. */
  readonly setArena: (arena: ArenaGeometry) => void;
  /** Update the LIVE keymap used to label target-hotkey nameplates (#136);
   *  a rebind pushes the new keymap so labels refresh immediately. */
  readonly setKeymap: (keymap: Keymap) => void;
  /** #855: update which incoming chat this viewer renders. Live, like
   *  `setKeymap` — toggling chat off in the settings overlay silences the
   *  next snapshot, not the next match. See `ChatDisplayPolicy`. */
  readonly setChatPolicy: (policy: ChatDisplayPolicy) => void;
  /**
   * #855: spawn a chat floater for the LOCAL player's own message, now,
   * without waiting for (or requiring) a server broadcast.
   *
   * ⚠️ This is what makes the server's silent reject actually silent. The
   * server may drop a message with no reply at all; if the sender's own line
   * were rendered off the returning snapshot, its absence would be a perfect
   * oracle for probing the filter. So the sender's echo is local and
   * unconditional, and `pushSnapshot` correspondingly DROPS the server's echo
   * of the local seat — the two halves must ship together or the message
   * double-renders.
   *
   * No-op when the local seat has no visible entity to anchor to (pre-match,
   * seat unknown, speaker not in this snapshot) — the same "nothing to anchor
   * to, so nothing is spawned" contract `ingestChat` has.
   */
  readonly echoLocalChat: (text: string) => void;
  /** Live camera center — read by input hit-testing each event so the
   *  clickable coords track the follow camera. */
  readonly getCenter: () => ProjectionCenter;
}

/** Options bag for `mountRenderer`. Keep small — this is the API surface. */
export interface MountRendererOptions {
  /** DOM container to mount into. `resizeTo` uses this. */
  readonly container: HTMLElement;
  /** Static arena geometry (from content). */
  readonly arena: ArenaGeometry;
  /** Sim tick rate — clock advance rate. */
  readonly tickRate: number;
  /** Interpolation delay (default 2). */
  readonly interpolationDelayTicks?: number;
  /** Snapshot source. Optional — callers may push snapshots via the
   *  returned `pushSnapshot` instead (tests / demo). */
  readonly snapshotSource?: SnapshotSource;
  /** Own seat index — the camera follows this seat's entity in "follow"
   *  mode. May arrive after mount; update via `Renderer.setOwnSeat`. Omitted
   *  or `null` means "not yet known" (#731) — NEVER coerced to seat 0; the
   *  camera holds arena center until a real seat is pushed. */
  readonly ownSeat?: number | null;
  /** #855: initial chat display policy. Defaults to
   *  {@link DEFAULT_CHAT_DISPLAY_POLICY}; push updates with
   *  `Renderer.setChatPolicy`. */
  readonly chatPolicy?: ChatDisplayPolicy;
  /** Camera behavior. Default "follow" (player-follow + edge clamp).
   *  The demo harness / visual baseline pass "arenaCenter" for a static
   *  view so whole-scene screenshots stay deterministic. */
  readonly cameraMode?: CameraMode;
  /** Internal presentation cadence in simulation ticks; not user-facing. */
  readonly graphicsCadenceTicks?: number;
  /**
   * Optional injection: get wall-clock milliseconds. Defaults to
   * `performance.now`. Tests pass a fake to make the render loop
   * deterministic.
   */
  readonly now?: () => number;
  /**
   * Auto-run a rAF loop after mount? Default: true. Set to `false`
   * from tests / Playwright and drive rendering with `renderOnce()`.
   */
  readonly autoLoop?: boolean;
  /**
   * Floating-combat-text treatment (#48). Fully customizable; defaults to
   * `DEFAULT_COMBAT_TEXT_STYLE`. Swap for a themed/settings-driven style.
   */
  readonly combatTextStyle?: CombatTextStyle;
  /**
   * Target-hotkey nameplate treatment (#136). Fully customizable; defaults to
   * `DEFAULT_NAMEPLATE_STYLE`. Swap for a themed/settings-driven style.
   */
  readonly nameplateStyle?: NameplateStyle;
  /**
   * Casting-spell name treatment (#836) for the world-space above-head stack.
   * Fully customizable; defaults to `DEFAULT_SPELL_NAME_STYLE`. Swap for a
   * themed/settings-driven style.
   */
  readonly spellNameStyle?: SpellNameStyle;
  /**
   * Initial keymap for target-hotkey nameplate labels (#136). Defaults to
   * `DEFAULT_KEYMAP`; the React shell pushes the LIVE resolved keymap via
   * `Renderer.setKeymap` so rebinds refresh the labels.
   */
  readonly keymap?: Keymap;
  /**
   * Resolve a seat to its champion id so the renderer can pick that champion's
   * sprite sheet (#223 Task 4), 3D model (#237, per-seat since #244), or
   * above-head resource-bar pool ceiling/color (#337/#344). Called for EVERY
   * seat present in a frame, not just the local one — callers should back
   * this with a full seat→champion roster (the wire `matchPhase.roster`,
   * #244), not just the local selection. Cosmetic/presentation only. Omitted
   * (the default), or a seat resolving to `null`, → that entity uses the
   * procedural 2D body (the rip-out contract) and shows no resource bar.
   */
  readonly championIdForSeat?: (seat: number) => ChampionId | null;
  /**
   * #795: impact-audio player (a `SoundEngineApi` slice). Provided → this
   * renderer plays an ability's committed `.mp3` when its damage LANDS,
   * driven off the same once-per-snapshot `events` drain combat text uses
   * (see `ImpactAudioManager`). Omitted (the demo harness, tests, the visual
   * baseline) → no audio at all, exactly as before.
   */
  readonly impactAudio?: ImpactAudioPlayer;
}

/**
 * Mount the Pixi renderer into `container`. Returns a `Renderer`
 * handle. Async because Pixi v8's `Application.init` is async.
 */
export async function mountRenderer(options: MountRendererOptions): Promise<Renderer> {
  const stage = await createStage(options.container, options.arena);
  const buffer = new SnapshotBuffer();
  const clock = new InterpolationClock({
    tickRate: options.tickRate,
    delayTicks: options.interpolationDelayTicks ?? DEFAULT_INTERP_DELAY_TICKS,
  });
  const views = new Map<string, EntityView>();
  const projectileViews = new Map<string, ProjectileView>();
  const wallViews = new Map<string, WallView>();
  const combatText = new CombatTextManager(
    stage.layers.fx,
    options.combatTextStyle ?? DEFAULT_COMBAT_TEXT_STYLE,
  );
  // #577: locked-list ability cast/impact flashes + ground-AOE indicators.
  // Shares the `fx` layer with combat text/projectiles (draw order: whichever
  // reconciles last this frame draws on top — cosmetic only, no z-fighting
  // concern at this fidelity).
  const abilityVisuals = new AbilityVisualManager(stage.layers.fx);
  const projectileImpacts = new ProjectileImpactManager(stage.layers.fx);
  const meleeAttackAnimations = new MeleeAttackAnimationTracker();
  // #795: impact audio. `null` when no player was injected (demo/tests/visual
  // baseline) — the ingest below is then skipped entirely.
  const impactAudio =
    options.impactAudio === undefined ? null : new ImpactAudioManager(options.impactAudio);
  // #731: `null` (never a defaulted 0) means "seat not yet known" — every
  // `ownSeat`-keyed lookup below (`.find((e) => e.seat === ownSeat)`)
  // naturally yields "no match" for a `null` seat (a real seat is always a
  // number), so viewer-team/follow-camera/nameplate resolution all degrade
  // to their documented "unknown" behavior for free.
  let ownSeat: number | null = options.ownSeat ?? null;
  let chatPolicy: ChatDisplayPolicy = options.chatPolicy ?? DEFAULT_CHAT_DISPLAY_POLICY;
  let keymap: Keymap = options.keymap ?? DEFAULT_KEYMAP;
  const nameplateStyle: NameplateStyle = options.nameplateStyle ?? DEFAULT_NAMEPLATE_STYLE;
  const spellNameStyle: SpellNameStyle = options.spellNameStyle ?? DEFAULT_SPELL_NAME_STYLE;
  const cameraMode: CameraMode = options.cameraMode ?? "follow";
  const now = options.now ?? (() => performance.now());
  const graphicsCadence = createGraphicsCadence(options.graphicsCadenceTicks ?? 1);
  const championIdForSeat = options.championIdForSeat;
  // Resolve an entity to its loaded champion sheet (or null → procedural body).
  // Empty `championSheets` (no committed art) or no seat→champion hook → null.
  const resolveSprite = (entity: InterpolatedEntity): LoadedChampionSheet | null => {
    if (championIdForSeat === undefined) return null;
    const championId = championIdForSeat(entity.seat);
    if (championId === null) return null;
    return stage.championSheets.get(championId) ?? null;
  };

  // ── Runtime 3D characters (#237, first slice of #231; per-seat-aware #244) ──
  // Activate the Three layer the FIRST time any seat present in an incoming
  // snapshot resolves (via `championIdForSeat`) to a 3D-eligible champion —
  // own OR remote. This is deliberately LAZY rather than decided once at
  // mount: at mount time the local client only knows its OWN champion
  // synchronously (the seat→champion roster for every OTHER seat arrives over
  // the wire, at the earliest alongside the "countdown" `matchPhase` — itself
  // sent before the first snapshot broadcast, see `Room.startMatch`), so
  // gating on `ownSeat` alone would permanently miss an opponent-only 3D
  // champion. Once a load attempt starts it is NEVER retried — a failed/
  // absent GLB (or no 3D-eligible seat at all) permanently keeps Pixi on its
  // normal 2D auto-render path (the rip-out contract).
  let three: ThreeCharacterLayer | null = null;
  /** Champion id → its parsed GLB. Only champions whose model actually loaded
   *  appear here; the rest stay on the 2D renderer (#818). */
  let threeModels: ReadonlyMap<string, LoadedCharacterModel> = new Map();
  let threeSetupStarted = false;
  // #816: the parsed pillar prop GLB, or `null` while it hasn't loaded. #829
  // removed the 2D pillar path, so a FAILED load never leaves this `null`
  // quietly — `loadPillarProp` rejects and the failure is surfaced loudly
  // instead of degrading to invisible-but-colliding pillars.
  let pillarProp: LoadedPropModel | null = null;
  function maybeActivateThree(entities: readonly { seat: number }[]): void {
    if (threeSetupStarted || championIdForSeat === undefined) return;
    // #829: the Three layer also OWNS THE PILLARS, so an arena WITH pillars
    // needs it up even when no champion in the match has a 3D model (a Hunter
    // mirror, say). Before #829 that match drew 2D pillars; that fallback is
    // gone, so gating activation purely on 3D-eligible champions would render
    // invisible-but-colliding pillars — the exact failure mode this issue
    // exists to prevent.
    const needsProps = stage.arena.pillars.length > 0;
    if (!needsProps && !entities.some((e) => has3DModel(championIdForSeat(e.seat)))) return;
    threeSetupStarted = true;
    // #816: the pillar GLB fetch runs CONCURRENTLY with the character setup —
    // both are independent assets and neither failing may block the other.
    const propLoad = loadPillarProp();
    void setupThreeLayer()
      .then(async (setup) => {
        if (setup === null) {
          // No Three layer at all (no shared WebGL context). Still AWAIT the
          // GLB so an unusable asset surfaces here rather than being swallowed
          // by an abandoned promise — and say so when this arena HAS pillars,
          // because without the layer there is nothing left to draw them
          // (#829). Not thrown, unlike a bad asset: this is a client-capability
          // limit with no repo-side fix, and the match still runs in 2D.
          await propLoad;
          if (stage.arena.pillars.length > 0) {
            console.error(
              "[pit] FATAL: no 3D layer (WebGL context unavailable) — this map's" +
                " pillars COLLIDE but cannot be drawn (#829: pillars are 3D-only).",
            );
          }
          return;
        }
        three = setup.layer;
        threeModels = setup.models;
        // Stop Pixi's ticker-driven auto-render; the frame loop now composites
        // Pixi world-below (floor) → Three (characters + 3D pillars, depth-sorted
        // together, #816) → Pixi overlay-above (entities/fx/bars/UI) each frame
        // (#243).
        stage.app.stop();
        pillarProp = await propLoad;
        // #816: React StrictMode double-mounts (see `stage.ts`'s destroy comment
        // on the #353 black-screen bug), so this renderer can already be disposed
        // by the time the pillar GLB resolves. `syncPillarProps` redraws the
        // static arena art — on a destroyed stage that would touch destroyed Pixi
        // containers, so it self-guards on `disposed`.
        syncPillarProps();
      })
      .catch((err: unknown) => {
        // #829 (owner decision Q2, "fail loudly"): the 3D pillar path is
        // MANDATORY — the 2D sprite is gone, so an unusable `pillar.glb` can no
        // longer degrade to flat pillars. Log it once, loudly and actionably
        // (`PropAssetError`'s message says which failure and how to fix it),
        // then RETHROW so the rejection is not swallowed: it surfaces as an
        // unhandled rejection in the browser console, in Playwright's
        // `pageerror`, and in CI. The alternative — catching and carrying on —
        // is what ships an LFS pointer file as invisible-but-colliding walls.
        console.error("[pit] FATAL: 3D pillars cannot render:", err);
        throw err;
      });
  }
  /**
   * Push the arena's pillars into the Three scene as 3D props (#816). Called
   * once when both the Three layer and the pillar GLB are live, and again after
   * `setArena` swaps the map (the prop set is REPLACED wholesale — static map
   * data, never a per-frame reconcile).
   *
   * No-op until BOTH are available. That covers "not loaded YET" and "no
   * 3D-modelled champion in this match, so the Three layer never activated" —
   * NOT "the GLB was bad", which rejects loudly upstream (#829) rather than
   * leaving `pillarProp` quietly `null`. Also a no-op once this renderer is
   * disposed: the async GLB load can resolve after a StrictMode double-mount
   * already tore this instance down, and redrawing static art through destroyed
   * Pixi containers is exactly the class of bug #353 hit at this seam.
   */
  function syncPillarProps(): void {
    if (disposed) return;
    const layer = three;
    const model = pillarProp;
    if (layer === null || model === null) return;
    const def = prop3DFor("pillar");
    const props: Prop3DInput[] = stage.arena.pillars.map((pillar, index) => ({
      id: `pillar:${String(index)}`,
      model,
      wx: pillar.x,
      wy: pillar.y,
      // The collision radius from MAP DATA — the 3D column's base radius is
      // exactly this (`propModel.instantiateProp`), and its height is derived
      // from it (1.6 × diameter = 3.2 × radius, `props.ts` #829), never baked
      // into the art.
      radiusUnits: pillar.radius,
      heightUnits: propHeightUnits(def, pillar.radius),
    }));
    layer.setProps(props);
  }
  // An entity uses the 3D path when the layer is live AND its champion's model
  // actually loaded. Keying off the loaded map (not just registry opt-in) is what
  // makes per-champion rip-out work: one champion's missing GLB can't blank it out
  // or make it borrow another champion's mesh — it just stays 2D (#818).
  const modelForEntity = (entity: InterpolatedEntity): LoadedCharacterModel | null => {
    if (three === null || championIdForSeat === undefined) return null;
    const championId = championIdForSeat(entity.seat);
    if (championId === null) return null;
    return threeModels.get(championId) ?? null;
  };
  const use3D = (entity: InterpolatedEntity): boolean => modelForEntity(entity) !== null;
  // #337: resolve an entity's champion for the above-head resource bar's pool
  // ceiling/color lookup — the SAME roster hook `resolveSprite`/`use3D` use.
  const resolveChampionId = (entity: InterpolatedEntity): ChampionId | null =>
    championIdForSeat?.(entity.seat) ?? null;

  let lastMs = now();
  let lastWorldFrame: InterpolatedState | null = null;
  let rafHandle: number | null = null;
  let disposed = false;
  let loggedLoopError = false;

  const sourceHandle: SnapshotSourceHandle | null =
    options.snapshotSource?.subscribe(pushSnapshot) ?? null;

  const onVisibilityChange = (): void => {
    if (document.visibilityState === "visible") {
      const latest = buffer.latest();
      if (latest !== undefined) clock.snap(latest.tick);
      lastMs = now();
    }
  };
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibilityChange);
  }

  function pushSnapshot(entry: SnapshotEntry): void {
    buffer.push(entry);
    // #244: every incoming snapshot carries every seat's entity, so this is
    // the earliest point (own OR remote) a 3D-eligible champion can be known.
    // No-ops once a load attempt has started.
    maybeActivateThree(entry.state.entities);
    const entities = entry.state.entities;
    // #422: resolved once per snapshot, shared by combat-text's per-target
    // hide check below AND #577's per-caster hide check — the two can never
    // disagree about which entities are hidden from this viewer this tick.
    const viewerTeam = entities.find((x) => x.seat === ownSeat)?.team ?? null;
    // Ingest this snapshot's cosmetic combat events (#48) exactly once — NOT
    // per rendered frame. Anchor each floater to its target's current world
    // position from this same snapshot; skip events whose target isn't
    // present (already gone / not visible), OR whose target is currently
    // hidden from this viewer (#422 — a "Rooted!"/etc. floater over an
    // invisible enemy-stealthed unit would leak their presence just as much
    // as drawing their sprite would; `ccApplied` in particular can fire
    // without breaking stealth, unlike damage — see #91's break-on-damage
    // hooks, which only cover actual hp loss).
    const evs = entry.state.events;
    if (evs.length > 0) {
      combatText.ingest(
        evs,
        (targetId) => {
          const e = entities.find((x) => x.entityId === targetId);
          if (e === undefined) return null;
          if (isHiddenFromViewer(e.team, e.auras, viewerTeam, entry.state.tick)) return null;
          return { x: e.x, y: e.y };
        },
        now(),
      );
      // #795: impact audio off the SAME once-per-snapshot drain — an ability's
      // sound fires when its damage lands, not on the local cast keypress.
      // Reuses the #422 hide check above (same predicate, same tick) so a
      // hidden enemy-stealthed unit's presence can't leak through audio either.
      impactAudio?.ingest(entry.state.tick, evs, (targetId) => {
        const e = entities.find((x) => x.entityId === targetId);
        if (e === undefined) return false;
        return !isHiddenFromViewer(e.team, e.auras, viewerTeam, entry.state.tick);
      });
    }
    // #833: in-game chat — the SAME once-per-snapshot drain cadence as
    // `events` above, but keyed off the SPEAKING SEAT rather than a target
    // entity (chat rides its own wire array precisely because it is spoken BY
    // a seat, not inflicted ON a target). A speaker who isn't in this
    // snapshot, or who is stealth-hidden from this viewer (#422 — speech
    // above an invisible unit would leak its position exactly like a
    // "Rooted!" floater would), simply gets no floater: the message is
    // dropped, never crashed on.
    //
    // #855: `visibleChatForViewer` decides what this viewer renders — the
    // settings toggle, the per-seat mute list, and (load-bearing) the local
    // seat's own messages, which were already echoed locally at send time so
    // that a server-side silent drop stays unobservable. See that function.
    const chat = visibleChatForViewer(entry.state.chat, chatPolicy, ownSeat);
    if (chat.length > 0) {
      combatText.ingestChat(
        chat,
        (speakerSeat) => {
          const e = entities.find((x) => x.seat === speakerSeat);
          if (e === undefined) return null;
          if (isHiddenFromViewer(e.team, e.auras, viewerTeam, entry.state.tick)) return null;
          return { x: e.x, y: e.y };
        },
        now(),
      );
    }
    // #577: spawn any locked-list ability's flash/indicator for this snapshot —
    // newly-started casts (cast-edge) AND, since #804, this tick's instant-cast
    // `castEvents` (an instant is `idle` again before the snapshot exists, so
    // it has no cast edge at all). Ingested every snapshot (NOT per rendered
    // frame) — see `AbilityVisualManager.ingest`'s doc comment for why this
    // can't reuse the combat-text `events`-drain cadence. The WHOLE snapshot is
    // passed because both paths must read the same tick's entities+castEvents.
    // Same #422 stealth-leak guard as combat text above, applied to both paths:
    // a hidden (enemy-stealthed) caster's flash must not reveal their
    // position/cast either.
    abilityVisuals.ingest(entry.state, now(), (e) =>
      isHiddenFromViewer(e.team, e.auras, viewerTeam, entry.state.tick),
    );
    // Contacts are ephemeral and may outlive their projectile. Ingest every
    // snapshot, including ticks without ordinary damage events, before coalescing audio.
    const terrainCue = projectileImpacts.ingest(entry.state, now(), (e) =>
      isHiddenFromViewer(e.team, e.auras, viewerTeam, entry.state.tick),
    );
    if (terrainCue) options.impactAudio?.play("projectileTerrain");
    meleeAttackAnimations.ingest(entry.state, now());
  }

  function computeFrame(dtSeconds: number): InterpolatedState | null {
    const latest = buffer.latest();
    if (latest === undefined) return null;
    const rt = clock.step(dtSeconds, latest.tick);
    const look = buffer.lookup(rt);
    if (look.kind === "empty") return null;
    if (look.kind === "single") {
      const s: WireSimState = look.entry.state;
      return interpolate(s, s, 0, rt);
    }
    return interpolate(look.a.state, look.b.state, look.alpha, rt);
  }

  /**
   * Load EVERY registered champion GLB (#818 — rogue + fighter) and attach a
   * shared-context Three layer. Each champion's model is resolved per entity at
   * sync time, so a frame can mix champions.
   *
   * Per-champion rip-out: a model that fails to fetch/decode is simply absent
   * from the map, and `use3D` then keeps THAT champion on the 2D renderer while
   * the others still render in 3D.
   *
   * Returns `null` — never throws — when the shared context is unavailable, or
   * when no character model loaded AND this arena has no pillars to draw. With
   * pillars present the layer comes up even with zero character models (#829):
   * it is the only thing that can render them, and every champion simply stays
   * on the 2D renderer, exactly as in a mixed 2D/3D match.
   */
  async function setupThreeLayer(): Promise<{
    readonly layer: ThreeCharacterLayer;
    readonly models: ReadonlyMap<string, LoadedCharacterModel>;
  } | null> {
    const loaded = new Map<string, LoadedCharacterModel>();
    await Promise.all(
      CHAMPION_IDS_WITH_3D_MODEL.map(async (championId) => {
        const modelDef = model3DFor(championId);
        if (modelDef === null) return;
        try {
          const res = await fetch(model3DUrl(modelDef));
          if (!res.ok) return;
          const model = await parseCharacterModel(await res.arrayBuffer());
          if (model === null) return;
          loaded.set(championId, model);
        } catch {
          // Network / decode failure → this champion stays 2D.
        }
      }),
    );
    if (loaded.size === 0 && stage.arena.pillars.length === 0) return null;
    const layer = createThreeCharacterLayer(stage.app);
    if (layer === null) return null;
    return { layer, models: loaded };
  }

  /**
   * Load the pillar prop GLB (#816). REJECTS with a `PropAssetError` (#829) on a
   * missing asset (404), a network failure, a Git LFS pointer file, or
   * malformed/mesh-less bytes — every one of which used to fall back to the 2D
   * pillar sprite that no longer exists. The rejection is surfaced by
   * `maybeActivateThree`'s `.catch`.
   */
  function loadPillarProp(): Promise<LoadedPropModel> {
    return loadPropModel(prop3DUrl(prop3DFor("pillar")));
  }

  function renderOnce(): void {
    const t = now();
    const dt = (t - lastMs) / 1000;
    lastMs = t;
    const frame = computeFrame(dt);

    // Camera runs EVERY frame — even before the first snapshot — so it
    // keeps tracking window resizes while the arena border is shown.
    // Use app.screen.* (CSS px) not renderer.* (physical px): see WP10
    // Traps (DPR). The world layers are drawn at NO_OFFSET; the camera
    // is applied purely by scaling + translating the layer containers. #731: `ownSeat`
    // is `number | null` (never a defaulted 0) — a `null` seat never matches
    // any real entity's `.seat`, so `own` naturally resolves to `null` and
    // `computeCameraCenter` takes its documented "no player" branch (static
    // arena centering — see `camera.test.ts`'s "null player → static arena
    // centering" golden case), NOT seat 0's position.
    const own = frame?.entities.find((e) => e.seat === ownSeat)?.pos ?? null;
    const center =
      cameraMode === "follow"
        ? computeCameraCenter(
            own,
            stage.app.screen.width,
            stage.app.screen.height,
            stage.arena.width,
            stage.arena.height,
            // #781: carve the bottom HUD strip out of the canvas so the follow
            // camera's cover zoom AND its edge clamp both work over the ARENA
            // VIEWPORT — a champion at the map's south edge can no longer be
            // clamped behind the action bar (#679's reserve biased the centering
            // target only, which is exactly what let that happen). Scales with
            // canvas height, floored above the action bar's real footprint —
            // see `hudLayout.ts`. Y-axis only; the strip is bottom-anchored.
            hudReserveBottomPx(stage.app.screen.height),
          )
        : computeArenaCenter(
            stage.app.screen.width,
            stage.app.screen.height,
            stage.arena.width,
            stage.arena.height,
          );
    stage.center = center;
    // One place applies the viewport transform to the world layers (#730:
    // scale = cover zoom, position = centering offset) — see
    // `stage.applyCameraTransform`.
    applyCameraTransform(stage, center);

    // #422: the viewer's own team, resolved from THIS frame's roster (fixed
    // per match) — `null` until the viewer's own entity appears (fail open:
    // `isHidden` below never hides anything for a `null` viewer team).
    // Reused by both the 2D suppression (`renderFrame`'s `hidden` resolver)
    // and the 3D character sync filter below, so the two paths can never
    // disagree about which entities are hidden this frame.
    const viewerTeam = frame?.entities.find((e) => e.seat === ownSeat)?.team ?? null;
    const isHidden = (entity: InterpolatedEntity): boolean =>
      frame !== null && isHiddenFromViewer(entity.team, entity.auras, viewerTeam, frame.renderTick);
    // #923: the OTHER half of the same predicate pair — a stealthed entity
    // this viewer can still see (own champion / stealthed ally). Mutually
    // exclusive with `isHidden` by construction, and resolved here (not in
    // `entityView.ts`/`threeLayer.ts`) for the same reason `isHidden` is: the
    // render scope may not import `@pit/protocol` at runtime. Both renderers
    // read THIS value, so the 2D overlay and the 3D model can never disagree
    // about who is stealthed.
    const isStealthVisible = (entity: InterpolatedEntity): boolean =>
      frame !== null &&
      isStealthedVisibleToViewer(entity.team, entity.auras, viewerTeam, frame.renderTick);

    if (frame !== null) {
      const reconcileWorld = graphicsCadence.shouldReconcile(Math.floor(frame.renderTick));
      if (reconcileWorld) lastWorldFrame = frame;
      const worldFrame = lastWorldFrame ?? frame;
      // Walls (#488) draw on their own layer (also camera-offset via its
      // position, set above), reconciled every frame the same way projectiles
      // are — before entities so a placeholder wall never renders in front
      // of a character standing beside it.
      if (reconcileWorld) renderWalls(stage.layers.walls, wallViews, worldFrame, NO_OFFSET);
      // NO_OFFSET: the entities layer's position (set above) carries the
      // camera; passing stage.center here too would double-offset.
      // Nameplates (#136) resolve off the LATEST wire snapshot (roster is fixed
      // per match, so the interpolation delay is irrelevant to seat/team) + the
      // live keymap. `use3D` suppresses the 2D body for 3D champions (#237).
      //
      // Entities (and above-head health bars / nameplates) consume the fresh
      // interpolated `frame` on every rAF tick so that overhead UI and 2D/3D
      // models track camera and character translation in exact per-frame
      // lockstep with zero positional jitter (#1140).
      renderFrame(
        stage.layers.entities,
        views,
        frame,
        NO_OFFSET,
        {
          snapshot: buffer.latest()?.state ?? null,
          ownSeat,
          keymap,
          style: nameplateStyle,
        },
        {
          resolve: resolveSprite,
          clockMs: t,
          use3D,
          championId: resolveChampionId,
          hidden: isHidden,
          stealthed: isStealthVisible,
        },
        // #836: casting-spell name treatment for the above-head stack.
        spellNameStyle,
      );
      // Projectiles draw on the fx layer (also camera-offset via its position).
      // `viewerTeam` (#467): tints projectiles blue (own team) / red (enemy)
      // relative to the viewer, falling back to neutral white while `null`
      // (viewer's own team not yet resolved) — same `viewerTeam` computed
      // above for the 2D/3D hidden-entity filters.
      if (reconcileWorld)
        renderProjectiles(stage.layers.fx, projectileViews, worldFrame, NO_OFFSET, viewerTeam);
      // Floating combat text (#48) animates on the fx layer. NO_OFFSET anchor
      // + the fx container's camera position mean we pass NO_OFFSET here too,
      // matching projectiles (double-offset otherwise).
      combatText.update(t, NO_OFFSET);
      // #577: ability flashes/indicators animate on the same `fx` layer, same
      // NO_OFFSET-anchor reasoning as combat text above.
      abilityVisuals.update(t, NO_OFFSET);
      projectileImpacts.update(t, NO_OFFSET, (entityId) => {
        const entity = frame.entities.find((candidate) => candidate.entityId === entityId);
        if (entity === undefined || isHidden(entity)) return null;
        return {
          x: entity.pos.wx,
          y: entity.pos.wy,
          // The live render path knows whether the model loaded successfully;
          // a procedural fallback is centered on the ground, not a 3D torso.
          heightPx: use3D(entity) ? projectHeight(CHARACTER_TARGET_HEIGHT_UNITS * 0.55) : 0,
        };
      });
    }

    // ── 3D composite (#237, fixed #243, props #816). Pixi's auto-render is
    //    stopped while `three` is live, so THIS loop drives THREE passes each
    //    frame so the Three layer draws BETWEEN Pixi's world (floor/ground) and
    //    Pixi's overlay (entities/fx/UI) — not underneath the entire stage:
    //      1. Pixi `worldBelow` (floor + ground), clearing the canvas. The 2D
    //         pillar art is suppressed here once the 3D pillars are live (#816).
    //      2. Three characters AND static props (pillars) — one scene, so the
    //         GPU depth buffer occludes per-pixel: a champion behind a pillar is
    //         hidden, one in front of it is visible. Composited on top (color
    //         buffer preserved — see `threeLayer.renderScene`'s
    //         `autoClear = false`; only depth/stencil are cleared for a fresh
    //         3D pass, which is what makes the depth sort per-frame correct).
    //      3. Pixi `overlayAbove` (entities/fx/bars/nameplates), `clear: false`
    //         — NEVER depth-tested, so an occluded champion's health bar and
    //         nameplate still draw on top and stay locatable (#816).
    //    `resetState()` hands GL state cleanly between the two renderers (the
    //    official PixiJS "mixing three and pixi" pattern); Three does its own
    //    `resetState()` at the top of `renderScene()` before pass 2. ──
    if (three !== null) {
      // Three needs the fresh interpolated frame on every rAF: its walk/idle
      // state is inferred from consecutive positions, and its mixer advances
      // per rendered frame. lastWorldFrame deliberately coalesces expensive
      // 2D world reconciliation, so using it here would freeze those inputs
      // between cadence ticks and make moving characters fall back to idle.
      const chars: Character3DInput[] =
        frame === null
          ? []
          : frame.entities
              // #422: a hidden (enemy-stealthed) entity is excluded from the
              // 3D sync too — otherwise a 3D-modeled champion would leak its
              // presence through the Three layer even with the 2D `entityView`
              // correctly suppressed (the Three layer builds its OWN list
              // straight off `frame.entities`, bypassing entityView.ts
              // entirely).
              // Resolving the model in the same pass as the filter keeps the two in
              // lockstep — an entity is in the 3D list precisely because it HAS a
              // model, so there is no unchecked assertion here.
              .flatMap((e) => {
                if (isHidden(e)) return [];
                const model = modelForEntity(e);
                if (model === null) return [];
                return [
                  {
                    id: e.entityId,
                    wx: e.pos.wx,
                    wy: e.pos.wy,
                    facing: e.facing,
                    alive: e.alive,
                    // #825: tints the ground marker with `teamColor`, the same
                    // palette the 2D body fill uses.
                    team: e.team,
                    // Same derivation the 2D path uses (`entityView.ts`), so both
                    // renderers agree on when a champion is casting.
                    casting: e.cast !== null,
                    attacking: meleeAttackAnimations.isAttacking(e.entityId, now()),
                    // #923: a stealthed-but-visible champion (own/ally) is
                    // drawn see-through in 3D too — the Pixi root's alpha
                    // only dims the 2D overlay (bars/nameplate), and for a
                    // 3D champion the BODY is the thing that must read as
                    // "not fully here". Same `STEALTH_VISIBLE_ALPHA` the 2D
                    // path uses, so the two halves of one silhouette match.
                    opacity: isStealthVisible(e) ? STEALTH_VISIBLE_ALPHA : 1,
                    radius: e.radius,
                    model,
                  },
                ];
              });
      stage.app.renderer.render({ container: stage.layers.worldBelow, clear: true });
      three.syncCharacters(chars, dt);
      three.updateCamera(stage.app.screen.width, stage.app.screen.height, center);
      three.renderScene();
      stage.app.renderer.resetState();
      stage.app.renderer.render({ container: stage.layers.overlayAbove, clear: false });
    }
  }

  function loop(): void {
    if (disposed) return;
    try {
      renderOnce();
    } catch (err) {
      // A thrown error here previously killed the rAF chain permanently
      // (loop never rescheduled) → a silently frozen canvas while React/
      // HUD kept running. Log once and keep the loop alive so a transient
      // per-frame error (e.g. degenerate canvas size during mount) can
      // self-recover on the next frame.
      if (!loggedLoopError) {
        loggedLoopError = true;
        console.error("[pit] render loop error (loop kept alive):", err);
      }
    }
    rafHandle = requestAnimationFrame(loop);
  }

  if (options.autoLoop !== false && typeof requestAnimationFrame !== "undefined") {
    rafHandle = requestAnimationFrame(loop);
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    if (rafHandle !== null && typeof cancelAnimationFrame !== "undefined") {
      cancelAnimationFrame(rafHandle);
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }
    sourceHandle?.dispose();
    for (const [, view] of views) {
      view.root.destroy({ children: true });
    }
    views.clear();
    for (const [, pview] of projectileViews) {
      pview.root.destroy();
    }
    projectileViews.clear();
    for (const [, wview] of wallViews) {
      wview.root.destroy();
    }
    wallViews.clear();
    combatText.destroy();
    abilityVisuals.destroy();
    projectileImpacts.destroy();
    // Drop the Three renderer's own GPU resources BEFORE Pixi tears down the
    // shared context (Pixi owns it).
    three?.destroy();
    stage.destroy();
  }

  function setOwnSeat(seat: number | null): void {
    ownSeat = seat;
  }

  function setArena(arena: ArenaGeometry): void {
    setStageArena(stage, arena);
    // #816: the swapped map has its OWN pillars — re-push the 3D prop set so the
    // columns match the arena that was just drawn (no-op until the Three layer
    // and the pillar GLB are both live; there is no 2D pillar art to leave in
    // place since #829).
    syncPillarProps();
  }

  function setKeymap(next: Keymap): void {
    keymap = next;
  }

  function setChatPolicy(next: ChatDisplayPolicy): void {
    chatPolicy = next;
  }

  function echoLocalChat(text: string): void {
    // Anchored to the local seat's position in the most recent snapshot —
    // the same anchor the server echo would have used, one interpolation
    // delay earlier. Not gated on `chatPolicy.enabled`: with chat off the
    // compose box cannot open, so there is nothing to echo, and gating here
    // would only add a second place for that invariant to live.
    const latest = buffer.latest();
    if (latest === undefined) return;
    const own = latest.state.entities.find((e) => e.seat === ownSeat);
    if (own === undefined) return;
    combatText.ingestChat(
      [{ kind: "chat", seat: own.seat, text }],
      () => ({ x: own.x, y: own.y }),
      now(),
    );
  }

  function getCenter(): ProjectionCenter {
    return stage.center;
  }

  return {
    dispose,
    pushSnapshot,
    renderOnce,
    setOwnSeat,
    setArena,
    setKeymap,
    setChatPolicy,
    echoLocalChat,
    getCenter,
  };
}
